import { inject, singleton } from "tsyringe";
import { Action } from "@identity/services";
import {
  FandomContextGroups,
  ContextRepository,
  TopLevelDomainExtractor,
  WikiConfig,
  expiryDateFromMinutes,
} from "@identity/utils";
import { BeaconGenerator } from "../BeaconGenerator";
import { PageViewNumberGenerator } from "../PageViewNumberGenerator";
import { COOKIES, UUID, WINDOW } from "@identity/symbols";
import {
  CommunicationService,
  eventsRepository,
} from "../CommunicationService";
import { FANDOM_CONTEXT_UPDATE } from "@identity/custom_events";

@singleton()
export class TrackingContextSetupService {
  constructor(
    @inject(ContextRepository) private contextRepository: ContextRepository,
    @inject(COOKIES) private readonly cookiesManager: CookiesManager,
    @inject(UUID) private uuid: UuidProvider,
    @inject(BeaconGenerator) private beaconGenerator: BeaconGenerator,
    @inject(PageViewNumberGenerator)
    private pageViewNumberGenerator: PageViewNumberGenerator,
    @inject(WINDOW)
    private readonly window: Window,
    @inject(WikiConfig)
    private readonly wikiConfig: WikiConfig,
    @inject(CommunicationService)
    private readonly communicationService: CommunicationService,
  ) {
    this.communicationService.on(eventsRepository.PAGE_CHANGED, () => {
      this.updatePageViewData();
    });
  }

  private set<T extends keyof TrackingContext>(
    name: T,
    value: NonNullable<TrackingContext[T]>,
    cookieAttributes: CookieAttributes,
  ) {
    this.contextRepository.set(FandomContextGroups.tracking, name, value);
    this.cookiesManager.set(name, value.toString(), cookieAttributes);
  }

  private remove<T extends keyof TrackingContext>(name: T) {
    this.contextRepository.remove(FandomContextGroups.tracking, name);
    this.cookiesManager.remove(name);
  }

  private updatePageViewData() {
    const domain = this.window.location.hostname;
    const topLevelDomain = TopLevelDomainExtractor.extract(domain);
    this.contextRepository.set(
      FandomContextGroups.tracking,
      "pvUID",
      this.wikiConfig.get("pvUID") || // UCP
        this.window.pvUID || // F2
        this.uuid(),
    );

    this.set(
      "pvNumber",
      this.pageViewNumberGenerator.getNextNumberFromCookie("pvNumber"),
      {
        domain: domain,
        expires: expiryDateFromMinutes(30),
      },
    );

    this.set(
      "pvNumberGlobal",
      this.pageViewNumberGenerator.getNextNumberFromCookie("pvNumberGlobal"),
      {
        domain: topLevelDomain,
        expires: expiryDateFromMinutes(30),
      },
    );
  }

  private updateUserTrackingData() {
    const domain = this.window.location.hostname;
    const topLevelDomain = TopLevelDomainExtractor.extract(domain);
    const beaconId =
      this.cookiesManager.get("wikia_beacon_id") ||
      this.cookiesManager.get("beaconId");
    const beaconValue = beaconId || this.beaconGenerator.generate();
    this.contextRepository.set(
      FandomContextGroups.tracking,
      "beaconId",
      beaconValue,
    );
    this.cookiesManager.set("wikia_beacon_id", beaconValue, {
      domain: topLevelDomain,
      // TODO: use expiryDate(180, TimeUnit.DAYS) here and in other places
      expires: 180,
    });

    const b2 = this.cookiesManager.get("_b2");
    this.cookiesManager.set("_b2", b2 || this.beaconGenerator.generateV2(), {
      domain: topLevelDomain,
      expires: 730,
    });

    const mwSessionId = this.wikiConfig.get("sessionId", this.window.sessionId);
    const sessionId = mwSessionId || this.cookiesManager.get("sessionId");
    const sessionCookieParams = {
      domain: topLevelDomain,
      expires: expiryDateFromMinutes(30),
    };
    this.set("sessionId", sessionId || this.uuid(), sessionCookieParams);

    // This cookie was set by TrackingOptIn for 180 days with a beacon-like value.
    const wikiaSessionId = this.cookiesManager.get("wikia_session_id");
    this.cookiesManager.set(
      "wikia_session_id",
      wikiaSessionId || this.beaconGenerator.generate(),
      { domain: topLevelDomain, expires: 180 },
    );

    const userId =
      this.wikiConfig.get("userId") || this.window.RLCONF?.wgUserId || 0;
    this.contextRepository.set(FandomContextGroups.tracking, "userId", userId);
  }

  public setup(): void {
    this.updateUserTrackingData();
    this.updatePageViewData();
    this.communicationService.emit(
      new Action(eventsRepository.FANDOM_CONTEXT_SET),
    );

    const event = new CustomEvent(FANDOM_CONTEXT_UPDATE);
    this.window.dispatchEvent(event);
  }

  public clear(): void {
    this.remove("beaconId");
    this.remove("sessionId");
    this.remove("pvNumber");
    this.remove("pvNumberGlobal");
  }
}
